import React, { useEffect, useState } from "react";
import { set, useForm } from "react-hook-form";
import { HashLoader } from "react-spinners";
import UserService from "../../services/userService";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { decrypt } from "../../helper/EncryptionHelper";

interface RegisterProps {}

const ResetPassword: React.FC<RegisterProps> = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [buttonClickable, setButtonClickable] = useState(true);
  // const { userId } = useParams<{ userId: string }>();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [userid, setUserId] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    watch,
    trigger,
    formState: { errors },
  } = useForm({ mode: "onChange" });

  const password = watch("password");

  useEffect(() => {

    if (token) {
      try {
        const decodedToken = decodeURIComponent(
          token.toString().trim().replace(/\s+/g, "")
        );
        const decrypted = decrypt(decodedToken);
        const parsed = JSON.parse(decrypted);
        const expireOn = new Date(parsed.expireOn);

        if (expireOn < new Date()) {
          navigate("/signin", { replace: true });
          setTimeout(() => {
            toast.error("Reset link has expired.");
          }, 100);
          return;
        }

        setUserId(parsed.userId);
      } catch (e) {
        // console.error("Decryption error:", e);
        navigate("/signin", { replace: true });
        setTimeout(() => {
          toast.error("Invalid reset link.");
        }, 100);
      }
    }
  }, [token]);

  useEffect(() => {
    if (password) {
      trigger("confirmPassword");
    }
  }, [password, trigger]);

  const showSuccessToast = () =>
    toast.success("Password changed successfully", { autoClose: 3000 });
  const showErrorToast = (msg) => toast.error(msg, { autoClose: 10000 });

  const onSubmit = async (data: any) => {
    setLoading(true);
    setButtonClickable(false);
    try {
      var response = await UserService.resetPassword(userid, data.password);
      if (response) {
        setLoading(false);
        showSuccessToast();
        setTimeout(() => {
          navigate("/signin", { replace: true });
        }, 4000);
      } else {
        setLoading(false);
        setButtonClickable(true);
        showErrorToast("Reset password failed. Please try again.");
      }
    } catch (error) {
      setLoading(false);
      setButtonClickable(true);
      showErrorToast("Something went wrong. Please try again later.");
    }
  };

  return (
    <div className="min-h-screen bg-[url('https://iated.org/archive/iceri2022_pictures//iceri22-096.jpg')] bg-no-repeat bg-cover flex items-center justify-center">
      {loading && (
        <div className="absolute inset-0 bg-black bg-opacity-80 z-10">
          <div
            role="status"
            className="absolute -translate-x-1/2 -translate-y-1/2 top-2/4 left-1/2"
          >
            <HashLoader color={"white"} loading={loading} size={50} />
          </div>
        </div>
      )}
      <div className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4 max-w-md w-full">
        <h1 className="text-center text-2xl font-bold mb-6">Reset Password</h1>
        <div className="mx-6 text-center">
          Please enter your new password below. Make sure it's strong and
          secure.
        </div>
        <form>
          <div className="my-4">
            <label className="block text-gray-700 font-bold mb-2">
              Password
            </label>
            <input
              className="w-full px-5 py-3 rounded-lg font-medium bg-gray-100 border border-gray-200 placeholder-gray-500 text-sm focus:outline-none focus:border-gray-400 focus:bg-white"
              type="password"
              minLength={8}
              placeholder="Enter new password"
              {...register("password", {
                required: {
                  value: true,
                  message: "Password is required",
                },
                minLength: {
                  value: 8,
                  message: "Password must be at least 8 characters",
                },
              })}
            />
            {errors?.password?.message && (
              <p className="text-red-500 text-xs mt-2">
                {errors.password.message.toString()}
              </p>
            )}
          </div>

          <div className="my-4">
            <label className="block text-gray-700 font-bold mb-2">
              Confirm Password
            </label>
            <input
              className="w-full px-5 py-3 rounded-lg font-medium bg-gray-100 border border-gray-200 placeholder-gray-500 text-sm focus:outline-none focus:border-gray-400 focus:bg-white"
              type="password"
              placeholder="Confirm new password"
              {...register("confirmPassword", {
                validate: {
                  required: (value) =>
                    value !== "" || "Confirm Password is required",
                  match: (value) =>
                    value === password || "Passwords do not match",
                },
              })}
            />
            {errors?.confirmPassword?.message && (
              <p className="text-red-500 text-xs mt-2">
                {errors.confirmPassword.message.toString()}
              </p>
            )}
          </div>

          <button
            onClick={handleSubmit(onSubmit)}
            disabled={!buttonClickable}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline w-full"
            type="submit"
          >
            Reset Password
          </button>
        </form>
        <div className="flex flex-row my-4 items-center justify-center">
          Know your Password?
          <a href="/signin">
            <span className="text-[#625FFB] font-bold cursor-pointer ml-1">
              Login!
            </span>
          </a>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default ResetPassword;
